/**
 * AdminManualBooking — form to create a corporate / B2B booking that
 * bypasses the customer flow and the pricing engine.
 *
 * The admin enters trip details, a Bill-To (optional, for corporate GST
 * invoices), and the fare breakdown (base + night surcharge + tolls).
 * On submit the booking is created with status = COMPLETED and
 * payment_status = paid, so the invoice PDF is immediately downloadable
 * from the booking detail page.
 */
import { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Building2, Loader2, ReceiptText, User, Car, Wallet, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ADMIN_BOOKINGS_URL, adminBookingDetailUrl } from "@/constants/adminRoutes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RIDE_TYPES = [
  { value: "airport_pickup", label: "Airport Pickup" },
  { value: "airport_drop", label: "Airport Drop" },
  { value: "in_city", label: "In-City (Bengaluru)" },
  { value: "non_airport_city", label: "Non-Airport City" },
  { value: "outstation", label: "Outstation" },
];

const PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "corporate_invoice", label: "Corporate Invoice (Net 30)" },
  { value: "upi_offline", label: "UPI (Offline QR)" },
  { value: "cash", label: "Cash" },
];

// ── Date/time canonicalisation — mirrors AdminBookingDetail helpers ────
function toCanonicalDate(htmlDate) {
  if (!htmlDate) return "";
  const [y, m, d] = htmlDate.split("-").map(Number);
  if (!y || !m || !d) return "";
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function toCanonicalTime(htmlTime) {
  if (!htmlTime) return "";
  const [hh, mm] = htmlTime.split(":").map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return "";
  const dt = new Date(2000, 0, 1, hh, mm);
  return dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

// Small labeled input wrapper (matches AdminReports card visual language)
function Field({ label, required, hint, children, testId }) {
  return (
    <div>
      <label className="text-sarathi-text-secondary text-[11px] uppercase tracking-wider font-semibold block mb-1.5">
        {label}{required && <span className="text-sarathi-primary ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-sarathi-text-muted text-[11px] mt-1" data-testid={testId ? `${testId}-hint` : undefined}>{hint}</p>}
    </div>
  );
}

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <div className="bg-sarathi-surface border border-white/10 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-sarathi-primary/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-sarathi-primary" />
        </div>
        <div>
          <h2 className="text-white font-semibold text-sm">{title}</h2>
          {description && <p className="text-sarathi-text-muted text-[11px]">{description}</p>}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

const inputCls = "bg-sarathi-background border-white/10 text-white rounded-lg h-9 text-sm";

export default function AdminManualBooking() {
  const navigate = useNavigate();

  // Passenger
  const [passengerName, setPassengerName] = useState("");
  const [passengerPhone, setPassengerPhone] = useState("");
  const [passengerEmail, setPassengerEmail] = useState("");

  // Trip
  const [rideType, setRideType] = useState("airport_pickup");
  const [pickup, setPickup] = useState("");
  const [drop, setDrop] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [flightNumber, setFlightNumber] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");

  // Driver / Vehicle
  const [driverName, setDriverName] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [vehicleName, setVehicleName] = useState("");
  const [vehicleReg, setVehicleReg] = useState("");

  // Fare
  const [baseFare, setBaseFare] = useState("");
  const [nightSurcharge, setNightSurcharge] = useState("");
  const [tollSadahalli, setTollSadahalli] = useState("");
  const [tollMysurExp, setTollMysurExp] = useState("");
  const [tollNiceRoad, setTollNiceRoad] = useState("");

  // Corporate Bill To
  const [corporate, setCorporate] = useState(false);
  const [company, setCompany] = useState("");
  const [gstin, setGstin] = useState("");
  const [cin, setCin] = useState("");
  const [billAddress, setBillAddress] = useState("");

  // Payment
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);

  // Live total preview. Base + Surcharge are GST-INCLUSIVE (5%) — the
  // invoice PDF backs the GST out so we mirror that math here for parity.
  const totals = useMemo(() => {
    const num = (v) => {
      const n = parseFloat(String(v).replace(/[,₹\s]/g, ""));
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
    const base = num(baseFare);
    const surch = num(nightSurcharge);
    const t1 = num(tollSadahalli);
    const t2 = num(tollMysurExp);
    const t3 = num(tollNiceRoad);
    const tollTotal = t1 + t2 + t3;
    const taxInclusive = base + surch;                 // what the admin enters
    const taxable = Math.round((taxInclusive / 1.05) * 100) / 100;
    const gst = Math.round((taxInclusive - taxable) * 100) / 100;
    const cgst = Math.round((gst / 2) * 100) / 100;
    const sgst = Math.round((gst - cgst) * 100) / 100; // absorb rounding remainder
    const total = taxInclusive + tollTotal;
    return { base, surch, tollTotal, taxable, cgst, sgst, total };
  }, [baseFare, nightSurcharge, tollSadahalli, tollMysurExp, tollNiceRoad]);

  const submit = async () => {
    // ── Client-side validation (mirrors backend) ─────────────────────
    if (!passengerName.trim()) { toast.error("Passenger name is required"); return; }
    if (!pickup.trim() || !drop.trim()) { toast.error("Pickup and drop locations are required"); return; }
    if (!date || !time) { toast.error("Date and time are required"); return; }
    if (totals.base <= 0) { toast.error("Base fare must be greater than zero"); return; }
    const canonicalDate = toCanonicalDate(date);
    const canonicalTime = toCanonicalTime(time);
    if (!canonicalDate || !canonicalTime) { toast.error("Couldn't parse date/time"); return; }

    if (corporate && !company.trim()) {
      toast.error("Company name is required when Bill To is enabled");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        passenger_name: passengerName.trim(),
        passenger_phone: passengerPhone.trim() || null,
        passenger_email: passengerEmail.trim() || null,
        ride_type: rideType,
        pickup_location: pickup.trim(),
        drop_location: drop.trim(),
        date: canonicalDate,
        time: canonicalTime,
        passengers: parseInt(passengers, 10) || 1,
        flight_number: flightNumber.trim() || null,
        special_requests: specialRequests.trim() || null,
        driver_name: driverName.trim() || null,
        driver_phone: driverPhone.trim() || null,
        vehicle_name: vehicleName.trim() || null,
        vehicle_registration: vehicleReg.trim() || null,
        bill_to: corporate ? {
          company: company.trim(),
          gstin: gstin.trim() || null,
          cin: cin.trim() || null,
          address: billAddress.trim() || null,
        } : null,
        base_fare: totals.base,
        night_surcharge: totals.surch,
        toll_sadahalli: parseFloat(tollSadahalli) || 0,
        toll_mysur_exp: parseFloat(tollMysurExp) || 0,
        toll_nice_road: parseFloat(tollNiceRoad) || 0,
        payment_method: paymentMethod,
        payment_notes: paymentNotes.trim() || null,
        admin_notes: adminNotes.trim() || null,
      };

      const res = await axios.post(`${API}/admin/bookings/manual`, payload);
      const created = res.data;
      toast.success(`Booking ${created.booking_reference} created — total ₹${totals.total.toLocaleString("en-IN")}`);
      navigate(adminBookingDetailUrl(created.id));
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error(e?.response?.data?.detail || "Couldn't create manual booking.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-4xl" data-testid="admin-manual-booking-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link to={ADMIN_BOOKINGS_URL} className="text-sarathi-text-muted text-xs inline-flex items-center gap-1 hover:text-white transition-colors mb-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to bookings
          </Link>
          <div className="flex items-center gap-2">
            <ReceiptText className="w-5 h-5 text-sarathi-primary" />
            <h1 className="text-white text-xl sm:text-2xl font-bold">New Manual Booking</h1>
          </div>
          <p className="text-sarathi-text-muted text-sm mt-1">
            For corporate / offline rides. Booking will be marked COMPLETED + PAID so the GST invoice PDF is immediately downloadable.
          </p>
        </div>
      </div>

      {/* Passenger */}
      <SectionCard icon={User} title="Passenger" description="Who rode. Phone is optional and stored encrypted.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Passenger Name" required>
            <Input value={passengerName} onChange={(e) => setPassengerName(e.target.value)} className={inputCls} data-testid="mb-passenger-name" />
          </Field>
          <Field label="Passenger Phone" hint="10-digit, or with +91 prefix">
            <Input value={passengerPhone} onChange={(e) => setPassengerPhone(e.target.value)} className={inputCls} data-testid="mb-passenger-phone" />
          </Field>
          <Field label="Passenger Email">
            <Input type="email" value={passengerEmail} onChange={(e) => setPassengerEmail(e.target.value)} className={inputCls} data-testid="mb-passenger-email" />
          </Field>
        </div>
      </SectionCard>

      {/* Trip */}
      <SectionCard icon={MapPin} title="Trip Details" description="You can back-date past rides; the 90-day guardrail is bypassed for manual bookings.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Ride Type" required>
            <Select value={rideType} onValueChange={setRideType}>
              <SelectTrigger className={inputCls} data-testid="mb-ride-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RIDE_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Passengers" hint="1-10">
            <Input type="number" min={1} max={10} value={passengers} onChange={(e) => setPassengers(e.target.value)} className={inputCls} data-testid="mb-passengers" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Pickup Location" required>
              <Input value={pickup} onChange={(e) => setPickup(e.target.value)} className={inputCls} data-testid="mb-pickup" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Drop Location" required>
              <Input value={drop} onChange={(e) => setDrop(e.target.value)} className={inputCls} data-testid="mb-drop" />
            </Field>
          </div>
          <Field label="Date" required>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="mb-date" />
          </Field>
          <Field label="Time" required>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} data-testid="mb-time" />
          </Field>
          <Field label="Flight Number">
            <Input value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} className={inputCls} data-testid="mb-flight-number" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Special Requests">
              <Textarea value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} rows={2} className="bg-sarathi-background border-white/10 text-white rounded-lg text-sm" data-testid="mb-special-requests" />
            </Field>
          </div>
        </div>
      </SectionCard>

      {/* Fare */}
      <SectionCard icon={Wallet} title="Fare Breakdown" description="Base + Night Surcharge are GST-inclusive (5% will be backed out on the invoice). Tolls are pass-through, not taxed.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Base Fare (₹)" required hint="GST-inclusive · 5% (CGST 2.5% + SGST 2.5%) will be backed out on the invoice">
            <Input type="number" step="0.01" min={0} value={baseFare} onChange={(e) => setBaseFare(e.target.value)} className={inputCls} data-testid="mb-base-fare" />
          </Field>
          <Field label="Night Surcharge (₹)" hint="Optional · GST-inclusive · taxed at 5% like the base fare">
            <Input type="number" step="0.01" min={0} value={nightSurcharge} onChange={(e) => setNightSurcharge(e.target.value)} className={inputCls} data-testid="mb-night-surcharge" />
          </Field>
          <Field label="Toll — Devanahalli / Sadahalli (₹)">
            <Input type="number" step="0.01" min={0} value={tollSadahalli} onChange={(e) => setTollSadahalli(e.target.value)} className={inputCls} data-testid="mb-toll-sadahalli" />
          </Field>
          <Field label="Toll — Bengaluru–Mysuru Expressway (₹)">
            <Input type="number" step="0.01" min={0} value={tollMysurExp} onChange={(e) => setTollMysurExp(e.target.value)} className={inputCls} data-testid="mb-toll-mysur" />
          </Field>
          <Field label="Toll — NICE Road (₹)">
            <Input type="number" step="0.01" min={0} value={tollNiceRoad} onChange={(e) => setTollNiceRoad(e.target.value)} className={inputCls} data-testid="mb-toll-nice" />
          </Field>
        </div>

        {/* Live total preview — mirrors the invoice PDF line-by-line */}
        <div className="bg-sarathi-background border border-white/5 rounded-lg p-4 mt-2 space-y-1" data-testid="mb-total-preview">
          <div className="flex items-center justify-between text-xs text-sarathi-text-muted">
            <span>Base + Surcharge (taxable, pre-tax)</span>
            <span data-testid="mb-taxable">₹{totals.taxable.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-sarathi-text-muted">
            <span>CGST @ 2.5%</span>
            <span data-testid="mb-cgst">₹{totals.cgst.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-sarathi-text-muted">
            <span>SGST @ 2.5%</span>
            <span data-testid="mb-sgst">₹{totals.sgst.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-sarathi-text-muted">
            <span>Tolls (pass-through, non-taxable)</span>
            <span>₹{totals.tollTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="border-t border-white/5 mt-2 pt-2 flex items-center justify-between">
            <span className="text-white text-sm font-semibold">Grand Total</span>
            <span className="text-sarathi-primary font-outfit font-bold text-lg" data-testid="mb-grand-total">
              ₹{totals.total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </SectionCard>

      {/* Corporate Bill To */}
      <SectionCard icon={Building2} title="Corporate Bill To" description="Enable to render company name, GSTIN, CIN and billing address on the invoice's Bill-To block.">
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={corporate}
              onChange={(e) => setCorporate(e.target.checked)}
              className="w-4 h-4 accent-sarathi-primary"
              data-testid="mb-corporate-toggle"
            />
            <span className="text-white text-sm">Bill to a company (corporate invoice)</span>
          </label>
        </div>
        {corporate && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Field label="Company Name" required>
                <Input value={company} onChange={(e) => setCompany(e.target.value)} className={inputCls} data-testid="mb-company" />
              </Field>
            </div>
            <Field label="GSTIN">
              <Input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} className={inputCls} data-testid="mb-gstin" />
            </Field>
            <Field label="CIN">
              <Input value={cin} onChange={(e) => setCin(e.target.value.toUpperCase())} className={inputCls} data-testid="mb-cin" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Billing Address">
                <Textarea value={billAddress} onChange={(e) => setBillAddress(e.target.value)} rows={2} className="bg-sarathi-background border-white/10 text-white rounded-lg text-sm" data-testid="mb-bill-address" />
              </Field>
            </div>
          </div>
        )}
      </SectionCard>

      {/* Driver / Vehicle */}
      <SectionCard icon={Car} title="Driver & Vehicle" description="Leave blank to auto-fill from Settings > Defaults.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Driver Name">
            <Input value={driverName} onChange={(e) => setDriverName(e.target.value)} className={inputCls} data-testid="mb-driver-name" />
          </Field>
          <Field label="Driver Phone">
            <Input value={driverPhone} onChange={(e) => setDriverPhone(e.target.value)} className={inputCls} data-testid="mb-driver-phone" />
          </Field>
          <Field label="Vehicle Name">
            <Input value={vehicleName} onChange={(e) => setVehicleName(e.target.value)} className={inputCls} data-testid="mb-vehicle-name" />
          </Field>
          <Field label="Vehicle Registration">
            <Input value={vehicleReg} onChange={(e) => setVehicleReg(e.target.value.toUpperCase())} className={inputCls} data-testid="mb-vehicle-reg" />
          </Field>
        </div>
      </SectionCard>

      {/* Payment */}
      <SectionCard icon={Wallet} title="Payment" description="Booking is marked PAID immediately — pick how the payment was received.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Payment Method" required>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger className={inputCls} data-testid="mb-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Payment Notes" hint="e.g. NEFT ref, cheque #, invoice due date">
            <Input value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} className={inputCls} data-testid="mb-payment-notes" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Admin Notes" hint="Internal only. Won't appear on the invoice.">
              <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={2} className="bg-sarathi-background border-white/10 text-white rounded-lg text-sm" data-testid="mb-admin-notes" />
            </Field>
          </div>
        </div>
      </SectionCard>

      {/* Submit */}
      <div className="flex items-center justify-end gap-3 pb-8">
        <Link to={ADMIN_BOOKINGS_URL}>
          <Button variant="outline" className="border-white/10 text-sarathi-text-secondary hover:text-white h-10" data-testid="mb-cancel-btn">
            Cancel
          </Button>
        </Link>
        <Button
          onClick={submit}
          disabled={submitting}
          className="bg-sarathi-primary hover:bg-sarathi-primary-hover text-white h-10 min-w-[180px]"
          data-testid="mb-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & Generate Invoice"}
        </Button>
      </div>
    </div>
  );
}
